// ============================================================
// PrintSettingsModal
// ============================================================
// Shown when the user clicks Print/PDF.
// Lets them choose a paper theme before window.print() fires.
// The chosen theme is applied as a data-print-theme attribute
// on <html>, which the print CSS uses to set paper/ink colors.
// ============================================================

import { useState } from 'react';

export type PrintTheme = 'clean' | 'manuscript' | 'parchment' | 'night' | 'blueprint';

interface PrintThemeOption {
  id: PrintTheme;
  label: string;
  paper: string;
  ink: string;
  rule: string;
  description: string;
}

const PRINT_THEMES: PrintThemeOption[] = [
  {
    id:          'clean',
    label:       'Clean',
    paper:       '#ffffff',
    ink:         '#000000',
    rule:        '#cccccc',
    description: 'White paper, black ink',
  },
  {
    id:          'manuscript',
    label:       'Manuscript',
    paper:       '#f9f6f0',
    ink:         '#1a1714',
    rule:        '#d8d0c4',
    description: 'Warm cream, like quality paper',
  },
  {
    id:          'parchment',
    label:       'Parchment',
    paper:       '#f5e6c8',
    ink:         '#2c1a08',
    rule:        '#d4b88a',
    description: 'Aged amber, deep brown ink',
  },
  {
    id:          'night',
    label:       'Night',
    paper:       '#12141a',
    ink:         '#e8dfc8',
    rule:        '#2a2d3a',
    description: 'Dark paper, warm ink',
  },
  {
    id:          'blueprint',
    label:       'Blueprint',
    paper:       '#0d1b2a',
    ink:         '#cfe8ff',
    rule:        '#1a3a54',
    description: 'Navy paper, cyan ink',
  },
];

interface PrintSettingsModalProps {
  onClose: () => void;
}

export function PrintSettingsModal({ onClose }: PrintSettingsModalProps) {
  const [selected, setSelected] = useState<PrintTheme>('clean');

  const handlePrint = () => {
    const theme = PRINT_THEMES.find((t) => t.id === selected)!;

    // Inject a full-page background element — this prints reliably
    // even when "Print backgrounds" is off in the browser dialog,
    // because it's treated as content rather than a CSS background.
    const bg = document.createElement('div');
    bg.id = '__print-bg__';
    bg.style.cssText = `
      position: fixed;
      inset: 0;
      background: ${theme.paper};
      z-index: -1;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    `;
    document.body.appendChild(bg);

    // Also inject a <style> block with the ink colors for this theme
    const style = document.createElement('style');
    style.id = '__print-theme-style__';
    style.textContent = `
      @media print {
        :root {
          --print-paper:     ${theme.paper} !important;
          --print-ink:       ${theme.ink} !important;
          --print-ink-2:     ${theme.ink} !important;
          --print-ink-muted: ${theme.rule} !important;
          --print-rule:      ${theme.rule} !important;
        }
        body, html {
          background: ${theme.paper} !important;
          color: ${theme.ink} !important;
        }
        #__print-bg__ {
          display: block !important;
          position: fixed !important;
          inset: 0 !important;
          background: ${theme.paper} !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Set the data attribute too for any CSS variable consumers
    document.documentElement.setAttribute('data-print-theme', selected);

    setTimeout(() => {
      window.print();
      // Clean up after print dialog closes
      setTimeout(() => {
        document.getElementById('__print-bg__')?.remove();
        document.getElementById('__print-theme-style__')?.remove();
        document.documentElement.removeAttribute('data-print-theme');
      }, 1500);
    }, 80);

    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 600,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 700, width: 380,
          background: 'var(--color-paper)',
          border: '1px solid var(--color-rule)',
          borderRadius: 10,
          boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          padding: '24px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontWeight: 600,
              fontSize: 20, color: 'var(--color-ink-primary)', marginBottom: 2,
            }}>
              Print / Export PDF
            </h2>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-ink-muted)' }}>
              Choose a paper style
            </p>
          </div>
          <button onClick={onClose} className="control-btn">✕</button>
        </div>

        {/* Theme grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {PRINT_THEMES.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setSelected(theme.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px',
                borderRadius: 7,
                border: selected === theme.id
                  ? '1.5px solid var(--color-accent)'
                  : '1px solid var(--color-rule)',
                background: selected === theme.id
                  ? 'rgba(var(--color-accent-rgb), 0.06)'
                  : 'transparent',
                cursor: 'pointer',
                transition: 'all 120ms',
                textAlign: 'left',
              }}
            >
              {/* Paper preview swatch */}
              <div style={{
                width: 40, height: 28, borderRadius: 4, flexShrink: 0,
                background: theme.paper,
                border: '1px solid rgba(0,0,0,0.12)',
                position: 'relative', overflow: 'hidden',
              }}>
                {/* Mini staff lines */}
                {[8, 12, 16].map((y) => (
                  <div key={y} style={{
                    position: 'absolute', left: 4, right: 4, top: y,
                    height: 1, background: theme.rule, opacity: 0.8,
                  }} />
                ))}
                {/* Mini chord dot */}
                <div style={{
                  position: 'absolute', left: 10, top: 9,
                  width: 5, height: 5, borderRadius: '50%',
                  background: theme.ink, opacity: 0.9,
                }} />
                <div style={{
                  position: 'absolute', left: 22, top: 13,
                  width: 5, height: 5, borderRadius: '50%',
                  background: theme.ink, opacity: 0.9,
                }} />
              </div>

              {/* Label */}
              <div>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 13,
                  fontWeight: 500,
                  color: selected === theme.id
                    ? 'var(--color-accent)'
                    : 'var(--color-ink-primary)',
                }}>
                  {theme.label}
                </div>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 10,
                  color: 'var(--color-ink-muted)', marginTop: 1,
                }}>
                  {theme.description}
                </div>
              </div>

              {/* Selected indicator */}
              {selected === theme.id && (
                <div style={{
                  marginLeft: 'auto', width: 16, height: 16,
                  borderRadius: '50%', background: 'var(--color-accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ color: 'white', fontSize: 9, lineHeight: 1 }}>✓</span>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="metadata-badge">
            Cancel
          </button>
          <button
            onClick={handlePrint}
            style={{
              padding: '6px 18px',
              background: 'var(--color-accent)', color: 'white',
              border: 'none', borderRadius: 999,
              fontFamily: 'var(--font-mono)', fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Print / Save PDF
          </button>
        </div>
      </div>
    </>
  );
}